import { createApi } from '@reduxjs/toolkit/query/react';
import { overrideFetchBaseQuery } from '../lib/utils/overrideFetchBaseQuery';
import type {
  MonthlyStoreTarget,
  CreateMonthlyStoreTargetPayload,
  UpdateMonthlyStoreTargetPayload,
  WeeklyStoreTarget,
  CreateWeeklyStoreTargetPayload,
  UpdateWeeklyStoreTargetPayload,
  MonthlyStylistTarget,
  CreateMonthlyStylistTargetPayload,
  UpdateMonthlyStylistTargetPayload,
} from '@admin-panel/providers';

export const goalpostApi = createApi({
  reducerPath: 'goalpostApi',
  baseQuery: overrideFetchBaseQuery({ urlSuffix: 'goalpost' }),
  tagTypes: ['MonthlyStoreTarget', 'WeeklyStoreTarget', 'MonthlyStylistTarget'],
  endpoints: builder => ({
    // ── Monthly Store Targets ────────────────────────────────────────────────
    getMonthlyStoreTargets: builder.query<MonthlyStoreTarget[], void>({
      query: () => '/monthly-store-targets',
      providesTags: ['MonthlyStoreTarget'],
    }),
    createMonthlyStoreTarget: builder.mutation<MonthlyStoreTarget, CreateMonthlyStoreTargetPayload>(
      {
        query: body => ({ url: '/monthly-store-targets', method: 'POST', body }),
        invalidatesTags: ['MonthlyStoreTarget'],
      },
    ),
    updateMonthlyStoreTarget: builder.mutation<
      MonthlyStoreTarget,
      { id: string } & UpdateMonthlyStoreTargetPayload
    >({
      query: ({ id, ...body }) => ({ url: `/monthly-store-targets/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['MonthlyStoreTarget'],
    }),

    // ── Weekly Store Targets (IB & Non-Apparel) ──────────────────────────────
    getWeeklyStoreTargets: builder.query<WeeklyStoreTarget[], void>({
      query: () => '/weekly-store-targets',
      providesTags: ['WeeklyStoreTarget'],
    }),
    createWeeklyStoreTarget: builder.mutation<WeeklyStoreTarget, CreateWeeklyStoreTargetPayload>({
      query: body => ({ url: '/weekly-store-targets', method: 'POST', body }),
      invalidatesTags: ['WeeklyStoreTarget'],
    }),
    updateWeeklyStoreTarget: builder.mutation<
      WeeklyStoreTarget,
      { id: string } & UpdateWeeklyStoreTargetPayload
    >({
      query: ({ id, ...body }) => ({ url: `/weekly-store-targets/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['WeeklyStoreTarget'],
    }),

    // ── Monthly Store Targets (delete) ──────────────────────────────────────
    deleteMonthlyStoreTarget: builder.mutation<void, string>({
      query: id => ({ url: `/monthly-store-targets/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MonthlyStoreTarget'],
    }),

    // ── Weekly Store Targets (delete) ────────────────────────────────────────
    deleteWeeklyStoreTarget: builder.mutation<void, string>({
      query: id => ({ url: `/weekly-store-targets/${id}`, method: 'DELETE' }),
      invalidatesTags: ['WeeklyStoreTarget'],
    }),

    // ── Monthly Stylist Targets ──────────────────────────────────────────────
    getMonthlyStylistTargets: builder.query<MonthlyStylistTarget[], void>({
      query: () => '/monthly-stylist-targets',
      providesTags: ['MonthlyStylistTarget'],
    }),
    createMonthlyStylistTarget: builder.mutation<
      MonthlyStylistTarget,
      CreateMonthlyStylistTargetPayload
    >({
      query: body => ({ url: '/monthly-stylist-targets', method: 'POST', body }),
      invalidatesTags: ['MonthlyStylistTarget'],
    }),
    updateMonthlyStylistTarget: builder.mutation<
      MonthlyStylistTarget,
      { id: string } & UpdateMonthlyStylistTargetPayload
    >({
      query: ({ id, ...body }) => ({
        url: `/monthly-stylist-targets/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['MonthlyStylistTarget'],
    }),
    deleteMonthlyStylistTarget: builder.mutation<void, string>({
      query: id => ({ url: `/monthly-stylist-targets/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MonthlyStylistTarget'],
    }),
  }),
});

export const {
  useGetMonthlyStoreTargetsQuery,
  useCreateMonthlyStoreTargetMutation,
  useUpdateMonthlyStoreTargetMutation,
  useDeleteMonthlyStoreTargetMutation,
  useGetWeeklyStoreTargetsQuery,
  useCreateWeeklyStoreTargetMutation,
  useUpdateWeeklyStoreTargetMutation,
  useDeleteWeeklyStoreTargetMutation,
  useGetMonthlyStylistTargetsQuery,
  useCreateMonthlyStylistTargetMutation,
  useUpdateMonthlyStylistTargetMutation,
  useDeleteMonthlyStylistTargetMutation,
} = goalpostApi;
